import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Container, 
  Grid, 
  Typography, 
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  useTheme,
  InputAdornment,
  Badge,
  Tabs,
  Tab} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers';
import { 
  Add as AddIcon, 
  FilterList as FilterIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Payment as PaymentIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  EventNote as EventNoteIcon
} from '@mui/icons-material';
import { fetchBills } from '../../redux/thunks/billingThunks';
import { setFilters } from '../../redux/slices/billingSlice';
import { useUserData } from '../../hooks/useUserData';
import { useAuth } from '../../context/AuthContext';
import { selectSelectedFacility, setSelectedFacility } from '../../redux/slices/facilitiesSlice';
import ClaimsManagement from './ClaimsManagement';

// Styled Components - Consistent with other pages
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(102, 126, 234, 0.2)',
}));

const SearchCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  marginBottom: theme.spacing(3),
  border: `1px solid ${theme.palette.divider}`,
}));

const StatsCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
  },
}));

const CompactButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  padding: '8px 16px',
  minWidth: 'auto',
  backgroundColor: 'rgba(255, 255, 255, 0.15)',
  color: 'white',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    border: '1px solid rgba(255, 255, 255, 0.5)',
  },
}));

const StyledTable = styled(Table)(({ theme }) => ({
  '& .MuiTableCell-head': {
    backgroundColor: theme.palette.grey[50],
    fontWeight: 600,
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
  },
  '& .MuiTableCell-body': {
    fontSize: '0.875rem',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  '& .MuiTableRow-root:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const BillingDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { bills, loading, error, filters } = useSelector(state => state.billing);
  const selectedFacility = useSelector(selectSelectedFacility);
  const defaultFacility = useSelector(selectSelectedFacility);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  
  // Date filter state - default to last 6 months
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
  sixMonthsAgo.setHours(0, 0, 0, 0);
  const [invoiceStartDate, setInvoiceStartDate] = useState(sixMonthsAgo);
  const [invoiceEndDate, setInvoiceEndDate] = useState(new Date());
  
  // Get user data using the new hook
  const userData = useUserData();
  const { loading: authLoading } = useAuth();

  // Set selected facility to default on mount if not already set
  useEffect(() => {
    if (!selectedFacility && defaultFacility) {
      dispatch(setSelectedFacility(defaultFacility));
    }
  }, [selectedFacility, defaultFacility, dispatch]);

  useEffect(() => {
    // Only fetch bills if we have all required data and auth is loaded
    if (userData.user && selectedFacility && selectedFacility.id && !authLoading) {
      dispatch(fetchBills({ 
        user: userData.user,
        userRoles: userData.userRoles,
        uid: userData.uid,
        email: userData.email,
        name: userData.name,
        defaultFacility: userData.defaultFacility,
        facilityId: selectedFacility.id,
        startDate: invoiceStartDate?.toISOString(),
        endDate: invoiceEndDate?.toISOString()
      }));
    }
     
  }, [dispatch, userData.user, userData.userRoles, userData.uid, userData.email, userData.name, userData.defaultFacility, selectedFacility, authLoading, invoiceStartDate, invoiceEndDate]);

  // Calculate summary statistics
  const summaryStats = bills.reduce((acc, bill) => {
    acc.totalBilled += parseFloat(bill.totalAmount || 0);
    acc.totalPaid += parseFloat(bill.paidAmount || 0);
    acc.totalPending += parseFloat(bill.totalAmount || 0) - parseFloat(bill.paidAmount || 0);
    
    // Handle undefined status as 'pending'
    const status = bill.status || 'pending';
    acc[status] = (acc[status] || 0) + 1;
    
    return acc;
  }, {
    totalBilled: 0,
    totalPaid: 0,
    totalPending: 0,
    pending: 0,
    partial: 0,
    paid: 0
  });

  // Filter bills based on search term
  const filteredBills = bills.filter(bill =>
    (bill.patientName && bill.patientName.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (bill.billNumber && bill.billNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (bill.patientId && bill.patientId.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleFilterChange = (field, value) => {
    dispatch(setFilters({ [field]: value }));
  };

  const handleDateRangeChange = (field, date) => {
    dispatch(setFilters({ 
      dateRange: {
        ...filters.dateRange,
        [field]: date?.toISOString() || null
      }
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'partial':
        return 'warning';
      case 'pending':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircleIcon fontSize="small" />;
      case 'partial':
        return <WarningIcon fontSize="small" />;
      case 'pending':
        return <ScheduleIcon fontSize="small" />;
      default:
        return <ScheduleIcon fontSize="small" />;
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Reset page when search changes
  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  if (loading || authLoading) {
    return (
      <Box sx={{ p: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error && !authLoading) {
    return (
      <Box sx={{ p: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
        <Alert severity="error" sx={{ maxWidth: '600px', mx: 'auto' }}>
          Error: {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh', py: 3 }}>
      <Container maxWidth="lg">
        {/* Header Card */}
        <HeaderCard>
          <CardContent sx={{ py: 2, px: 3, '&:last-child': { pb: 2 } }}>
            <Grid container spacing={3} alignItems="center" justifyContent="space-between">
              <Grid item>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <AccountBalanceIcon sx={{ fontSize: 32, color: 'white' }} />
                  <Box>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
                      Billing Dashboard
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Manage patient billing and financial records
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <CompactButton
                    startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                    onClick={() => navigate('/billing/create')}
                    size="medium"
                  >
                    Create New Bill
                  </CompactButton>
                  <CompactButton
                    startIcon={<EventNoteIcon sx={{ fontSize: 18 }} />}
                    onClick={() => navigate('/billing/month-end-close')}
                    size="medium"
                  >
                    Month End Close
                  </CompactButton>
                  <Tooltip title="Toggle Filters">
                    <IconButton 
                      onClick={() => setShowFilters(!showFilters)}
                      sx={{ 
                        color: 'white', 
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' }
                      }}
                    >
                      <Badge badgeContent={Object.values(filters).filter(v => v && v !== 'all').length} color="error">
                        <FilterIcon />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </HeaderCard>

        {/* Search and Filters */}
        <SearchCard>
          <CardContent sx={{ py: 2, px: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search bills by patient name, bill number, or patient ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  size="small"
                />
              </Grid>
              {showFilters && (
                <>
                  <Grid item xs={12} md={2}>
                    <TextField
                      select
                      fullWidth
                      label="Status"
                      value={filters.status || 'all'}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      size="small"
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="partial">Partial</MenuItem>
                      <MenuItem value="paid">Paid</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <DatePicker
                      label="From Date"
                      value={filters.dateRange?.start || null}
                      onChange={(date) => handleDateRangeChange('start', date)}
                      renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <DatePicker
                      label="To Date"
                      value={filters.dateRange?.end || null}
                      onChange={(date) => handleDateRangeChange('end', date)}
                      renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </SearchCard>

        {/* Summary Statistics Cards - Compact */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={6} sm={6} md={3}>
            <StatsCard>
              <CardContent sx={{ textAlign: 'center', py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                  <Box sx={{ 
                    p: 1, 
                    borderRadius: '50%', 
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <TrendingUpIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography color="textSecondary" sx={{ fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.2 }}>
                      Total Billed
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1.2 }}>
                      ${summaryStats.totalBilled.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </StatsCard>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatsCard>
              <CardContent sx={{ textAlign: 'center', py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                  <Box sx={{ 
                    p: 1, 
                    borderRadius: '50%', 
                    backgroundColor: 'success.light',
                    color: 'success.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <CheckCircleIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography color="textSecondary" sx={{ fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.2 }}>
                      Total Paid
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main', lineHeight: 1.2 }}>
                      ${summaryStats.totalPaid.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </StatsCard>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatsCard>
              <CardContent sx={{ textAlign: 'center', py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                  <Box sx={{ 
                    p: 1, 
                    borderRadius: '50%', 
                    backgroundColor: 'warning.light',
                    color: 'warning.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <WarningIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography color="textSecondary" sx={{ fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.2 }}>
                      Total Pending
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'warning.main', lineHeight: 1.2 }}>
                      ${summaryStats.totalPending.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </StatsCard>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatsCard>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ 
                    p: 1, 
                    borderRadius: '50%', 
                    backgroundColor: 'info.light',
                    color: 'info.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <ReceiptIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography color="textSecondary" sx={{ fontSize: '0.75rem', fontWeight: 500, mb: 0.5 }}>
                      Bills Status
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip label={`${summaryStats.pending} Pending`} size="small" color="default" sx={{ height: 20, fontSize: '0.7rem' }} />
                      <Chip label={`${summaryStats.partial} Partial`} size="small" color="warning" sx={{ height: 20, fontSize: '0.7rem' }} />
                      <Chip label={`${summaryStats.paid} Paid`} size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </StatsCard>
          </Grid>
        </Grid>

        {/* Main Content Tabs */}
        <Card sx={{ borderRadius: '12px', boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)' }}>
          <CardContent sx={{ p: 0 }}>
            <Tabs 
              value={activeTab} 
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{ 
                borderBottom: 1, 
                borderColor: 'divider',
                px: 3,
                pt: 2
              }}
            >
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Invoices
                    <Chip 
                      label={filteredBills.length} 
                      size="small" 
                      color="primary" 
                      sx={{ height: 20, fontSize: '0.75rem' }}
                    />
                  </Box>
                }
                icon={<ReceiptIcon />} 
                iconPosition="start"
              />
              <Tab 
                label="Claims Management" 
                icon={<AssignmentIcon />} 
                iconPosition="start"
              />
            </Tabs>
            
            {/* Tab Content */}
            {activeTab === 0 && (
              <Box>
                {/* Date Range Filter */}
                <Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <DatePicker
                    label="From Date"
                    value={invoiceStartDate}
                    onChange={(date) => {
                      // Limit to max 6 months ago
                      const minDate = new Date();
                      minDate.setMonth(minDate.getMonth() - 6);
                      if (date && date >= minDate) {
                        setInvoiceStartDate(date);
                        setPage(0);
                      }
                    }}
                    minDate={(() => { const d = new Date(); d.setMonth(d.getMonth() - 6); return d; })()}
                    maxDate={invoiceEndDate || new Date()}
                    slotProps={{ textField: { size: 'small', sx: { width: 180 } } }}
                  />
                  <DatePicker
                    label="To Date"
                    value={invoiceEndDate}
                    onChange={(date) => {
                      if (date) {
                        setInvoiceEndDate(date);
                        setPage(0);
                      }
                    }}
                    minDate={invoiceStartDate}
                    maxDate={new Date()}
                    slotProps={{ textField: { size: 'small', sx: { width: 180 } } }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    (Last 6 months max)
                  </Typography>
                </Box>
                {filteredBills.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="textSecondary" variant="h6">
                      {bills.length === 0 ? 'No billing information available.' : 'No bills match your search criteria.'}
                    </Typography>
                    {bills.length === 0 && (
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/billing/create')}
                        sx={{ mt: 2 }}
                      >
                        Create Your First Bill
                      </Button>
                    )}
                  </Box>
                ) : (
                  <>
                  <TableContainer>
                    <StyledTable>
                      <TableHead>
                        <TableRow>
                          <TableCell>Patient</TableCell>
                          <TableCell>Bill Number</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell align="center">Status</TableCell>
                          <TableCell>Due Date</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredBills.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((bill) => (
                          <TableRow key={bill.id} hover>
                            <TableCell>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {bill.patientName || 'N/A'}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  MRN: {bill.patientMRN || 'N/A'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                                #{bill.billNumber || bill.id}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                ${parseFloat(bill.totalAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </Typography>
                              {bill.paidAmount > 0 && (
                                <Typography variant="caption" color="success.main">
                                  Paid: ${parseFloat(bill.paidAmount).toFixed(2)}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Chip 
                                icon={getStatusIcon(bill.status || 'pending')}
                                label={bill.status || 'pending'} 
                                color={getStatusColor(bill.status || 'pending')}
                                size="small"
                                sx={{ fontWeight: 500 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {bill.dueDate ? new Date(bill.dueDate).toLocaleDateString() : 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                <Tooltip title="View Details">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => navigate(`/billing/${bill.id}`)}
                                    sx={{ 
                                      backgroundColor: 'primary.light',
                                      color: 'primary.contrastText',
                                      '&:hover': { backgroundColor: 'primary.main' }
                                    }}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {(bill.status || 'pending') !== 'paid' && (
                                  <Tooltip title="Process Payment">
                                    <IconButton
                                      size="small"
                                      color="success"
                                      onClick={() => navigate(`/billing/${bill.id}?action=payment`)}
                                      sx={{ 
                                        backgroundColor: 'success.light',
                                        color: 'success.contrastText',
                                        '&:hover': { backgroundColor: 'success.main' }
                                      }}
                                    >
                                      <PaymentIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </StyledTable>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[10, 20, 50]}
                    component="div"
                    count={filteredBills.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                  </>
                )}
              </Box>
            )}
            
            {activeTab === 1 && (
              <Box sx={{ p: 3 }}>
                <ClaimsManagement bills={bills} />
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default BillingDashboard;
